import * as React from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'

import { Note } from '../FormComponents/Note'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { NextActionDate } from '../FormComponents/NextActionDate/NextActionDate'
import { ServiceSummary } from '../FormComponents/ServiceSummary/ServiceSummary'
import { ExpectedClosingDate } from '../FormComponents/ExpectedClosingDate/ExpectedClosingDate'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import useMediaQuery from '@mui/material/useMediaQuery'
import { NoteCode } from '@user/components/Notes/typings'

import { useSelector } from 'react-redux'
import { selectAllEntityFormSettings } from '@user/store/entityFormSettings'
import { useGetFieldActiveStates } from '@user/store/entityFormSettings/hooks/useGetFieldActiveStates'
import { entityNextActionDate } from '@user/components/Settings/EntityFormSettings/constants'
import { useSecondaryFormContainer } from './hooks/useSecondaryFormContainer'

type Props = {
  hideOptional: boolean
  billTo: LocationDTO
  branchId: string
  entityId: number
  serviceLocations: LocationDTO[]
  serviceLocationsLoading: boolean
  entityType: ENTITY_TYPES
  createNewOpp?: boolean
  noteCodes: NoteCode[]
}

export function SecondaryForm(props: Props) {
  const { hideOptional, noteCodes } = props
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  const tidalThemeActive = useTidalThemeActive()
  const isMobile = useMediaQuery('(max-width:900px)')

  const settings = useSelector(selectAllEntityFormSettings)
  const entitySettingsMap = useGetFieldActiveStates(settings, hideOptional)

  const [requiredNextActionDate, showNextActionDate] = entitySettingsMap.get(
    entityNextActionDate,
  ) ?? [false, true]

  const { secondaryFormContainerHeight } = useSecondaryFormContainer({
    isMobile,
    activeOtherSection: true,
    containerRef,
  })

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{
        position: 'sticky',
        top: '150px',
        height: isMobile ? undefined : secondaryFormContainerHeight,
        transition: 'all 400ms ease-out',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0)', // fully transparent initially
          borderRadius: '4px',
          transition: 'background-color 0.3s ease', // smooth fade
        },
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)', // fade in on hover
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },

        scrollbarWidth: 'thin', // keeps space reserved
        scrollbarColor: 'transparent transparent', // transparent thumb initially
        '&:hover': {
          scrollbarColor: 'rgba(0,0,0,0.4) transparent', // thumb visible on hover
        },
      }}
      ref={containerRef}
    >
      <Paper
        sx={{
          px: 3,
          py: 0.375,
          display: isMobile ? 'flex' : 'block',
          gap: isMobile ? 2 : undefined,
        }}
        variant="outlined"
      >
        <NextActionDate
          fieldName="nextActionDate"
          hideOptional={hideOptional}
          tidal={tidalThemeActive}
          isMobile={isMobile}
          show={showNextActionDate}
          required={requiredNextActionDate}
        />
        <ExpectedClosingDate
          fieldName="expectedClosingDate"
          tidal={tidalThemeActive}
          isMobile={isMobile}
          isNextActionDateShowing={showNextActionDate}
        />
      </Paper>
      <ServiceSummary {...props} />
      {!isMobile && (
        <Note
          hideOptional={hideOptional}
          variant="card"
          noteCodes={noteCodes}
        />
      )}
    </Box>
  )
}
